import type { AgentSubscriber, HttpAgent, Message } from "@ag-ui/client";
import { createSparkAgent, runWatchedTurn } from "@/lib/agui-transport";
import {
  isUuid,
  normalizeRoleplayCode,
  parseRunError,
  parseRunResult,
  parseSnapshot,
  ROLEPLAY_CONTRACT,
  RoleplayError,
  type NarratorFailure,
  type RoleplayErrorCode,
  type RunResult,
  type Snapshot,
} from "./contract";
import { isBlankText } from "./story";

// One turn (B5) or one owed narration (B6) of the roleplay game over the AG-UI transport (play contract 3, §4). The
// caller learns exactly one outcome, matching the contract's invariant (§4.3): `finished` means stored, `failed` means
// not stored, `unknown` means "ask with B4" (a stall, a dropped connection, a result this build cannot read, a run that
// finished without a snapshot of what it stored). The caller's own abort reports nothing.

export type NarrationPart = "turn" | "opening" | "closing";

export interface RoleplayRunHandlers {
  narration: (part: NarrationPart, delta: string) => void;
  snapshot: (snapshot: Snapshot) => void;
  progress: (phase: string) => void;
  finished: (result: RunResult) => void;
  // `reason` comes with a RUN_ERROR `narrator_failed` only: why the narrator gave up (125).
  failed: (code: RoleplayErrorCode, reason?: NarratorFailure) => void;
  unknown: () => void;
}

// A turn is the player's words (79), and since contract 3 none goes without them (96). The contract still takes a pole
// id beside them (§4.1, optional), but spark sends none: the scene's suggestions are orientation only, never a
// button (23.3).
export interface RoleplayTurn {
  gameId: string;
  // New for every attempt (§4.1): the log returns it, so a stalled turn can be found again.
  runId: string;
  text: string;
  expectedTurn: number;
  abortController: AbortController;
  idleMs?: number;
}

export interface RoleplayNarration {
  gameId: string;
  runId: string;
  expectedTurn: number;
  abortController: AbortController;
  idleMs?: number;
}

function agentFor(gameId: string, route: "turns" | "narration"): HttpAgent {
  if (!isUuid(gameId)) throw new RoleplayError("ROLEPLAY_INVALID_INPUT");
  return createSparkAgent(`ai/roleplay/games/${gameId}/${route}`);
}

export function createTurnAgent(gameId: string): HttpAgent {
  return agentFor(gameId, "turns");
}

export function createNarrationAgent(gameId: string): HttpAgent {
  return agentFor(gameId, "narration");
}

// The message ids of §4.3: the turn's reply, then the next scene's opening or the story's closing.
function partOf(messageId: string): NarrationPart | null {
  if (messageId.startsWith("narration_")) return "turn";
  if (messageId.startsWith("opening_")) return "opening";
  if (messageId.startsWith("closing_")) return "closing";
  return null;
}

function codeOf(payload: unknown): RoleplayErrorCode {
  const errors = (payload as { errors?: { extensions?: { code?: unknown } }[] } | null)?.errors;
  return normalizeRoleplayCode(Array.isArray(errors) ? errors[0]?.extensions?.code : undefined);
}

function checkIds(gameId: string, runId: string, expectedTurn: number): void {
  if (!isUuid(gameId) || !isUuid(runId) || !Number.isInteger(expectedTurn) || expectedTurn < 0) {
    throw new RoleplayError("ROLEPLAY_INVALID_INPUT");
  }
}

async function runRoleplay(
  agent: HttpAgent,
  run: {
    gameId: string;
    runId: string;
    messages: Message[];
    forwardedProps: Record<string, unknown>;
    parts: readonly NarrationPart[];
    abortController: AbortController;
    idleMs?: number;
  },
  on: RoleplayRunHandlers,
): Promise<void> {
  // HttpAgent keeps thread, state and messages between runs; each run sends exactly what the contract asks for.
  agent.threadId = run.gameId;
  agent.setState({});
  agent.setMessages(run.messages);

  let settled = false;
  const settle = (report: () => void) => {
    if (settled) return;
    settled = true;
    report();
  };
  // A stored run sends its snapshot before RUN_FINISHED (§4.3), but agent-service finishes one that failed after its
  // commit without it (RoleplayRuns.locked), and the page would keep the game as it was before the run.
  let readableSnapshot = false;
  let unreadableSnapshot = false;
  const open = new Map<string, NarrationPart>();

  const subscriber: AgentSubscriber = {
    onTextMessageStartEvent: ({ event }) => {
      const part = partOf(event.messageId);
      if (part && run.parts.includes(part)) open.set(event.messageId, part);
    },
    onTextMessageContentEvent: ({ event }) => {
      const part = open.get(event.messageId);
      if (part) on.narration(part, event.delta);
    },
    onStateSnapshotEvent: ({ event }) => {
      const snapshot = parseSnapshot(event.snapshot);
      // A snapshot of another game is as unreadable here as a malformed one.
      if (snapshot && snapshot.game.id.toLowerCase() === run.gameId.toLowerCase()) {
        readableSnapshot = true;
        on.snapshot(snapshot);
      } else {
        unreadableSnapshot = true;
      }
    },
    onCustomEvent: ({ event }) => {
      if (event.name !== "roleplay.progress") return;
      const phase = (event.value as { phase?: unknown } | null)?.phase;
      if (typeof phase === "string" && phase !== "") on.progress(phase);
    },
    onRunFinishedEvent: ({ event }) => {
      const result = parseRunResult(event.result);
      // Stored, but not readable here or never shown: B4 tells what was stored.
      settle(() => (result && readableSnapshot && !unreadableSnapshot ? on.finished(result) : on.unknown()));
    },
    onRunErrorEvent: ({ event }) => {
      // The RUN_ERROR the AG-UI client makes up when the caller aborts is not the server's (§5.2).
      if (event.code === "abort") return;
      const { code, reason } = parseRunError(event);
      settle(() => on.failed(code, reason));
    },
  };

  try {
    await runWatchedTurn(
      agent,
      {
        runId: run.runId,
        context: [],
        forwardedProps: run.forwardedProps,
        abortController: run.abortController,
        idleMs: run.idleMs,
        onStall: () => settle(on.unknown),
      },
      subscriber,
    );
  } catch (error) {
    // Only a roleplay refusal before the stream (§5.1, §5.2: agent-service's code passed through by the BFF, or the
    // BFF's own) says that nothing was stored. Every other answer, the BFF's 502 AGENT_ERROR among them, may have come
    // after the request reached agent-service, whose run goes on without the browser and may still store the turn;
    // whether it did is a question for B4 (spark!432).
    const failure = error as { status?: unknown; payload?: unknown } | null | undefined;
    const code = typeof failure?.status === "number" ? codeOf(failure.payload) : "UNKNOWN";
    settle(() => (code !== "UNKNOWN" ? on.failed(code) : on.unknown()));
    return;
  }
  if (!run.abortController.signal.aborted) settle(on.unknown);
}

/** B5: one turn, flowing on into the opening or closing it leads to. */
export async function runRoleplayTurn(agent: HttpAgent, turn: RoleplayTurn, on: RoleplayRunHandlers): Promise<void> {
  checkIds(turn.gameId, turn.runId, turn.expectedTurn);
  if (isBlankText(turn.text)) throw new RoleplayError("ROLEPLAY_TEXT_REQUIRED");
  await runRoleplay(
    agent,
    {
      gameId: turn.gameId,
      runId: turn.runId,
      messages: [{ id: crypto.randomUUID(), role: "user", content: turn.text }],
      forwardedProps: { roleplay: { contract: ROLEPLAY_CONTRACT, expectedTurn: turn.expectedTurn } },
      parts: ["turn", "opening", "closing"],
      abortController: turn.abortController,
      idleMs: turn.idleMs,
    },
    on,
  );
}

/** B6: tell the narration the story owes: a scene's opening or the story's closing. */
export async function runRoleplayNarration(
  agent: HttpAgent,
  narration: RoleplayNarration,
  on: RoleplayRunHandlers,
): Promise<void> {
  checkIds(narration.gameId, narration.runId, narration.expectedTurn);
  await runRoleplay(
    agent,
    {
      gameId: narration.gameId,
      runId: narration.runId,
      messages: [],
      forwardedProps: { roleplay: { contract: ROLEPLAY_CONTRACT, expectedTurn: narration.expectedTurn } },
      parts: ["opening", "closing"],
      abortController: narration.abortController,
      idleMs: narration.idleMs,
    },
    on,
  );
}
